import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory, Link } from 'react-router-dom';
import {
  AlertCircle,
  BadgeCheck,
  BadgeCross,
  InfoCircle,
  Link as LinkIcon,
  Pencil,
  Refresh,
} from '@saleshandy/icons';
import { Button } from '@saleshandy/design-system';
import HeaderBanner from '../header-banner';
import AdminSettingToggle from './components/admin-setting-toggle';
import Spinner from '../../../../shared/design-system/components/atoms/spinner';
import toaster, { Theme } from '../../../../shared/toaster';
import { showGeneralErrorNotification } from '../../../../shared/utils/errors';
import {
  executeOnRequestStatus,
  executeOnErrorWithErrorCheck,
} from '../../../../shared/utils/execute-on-request-status';
import { SubscriptionPlans } from '../../../../shared/utils/subscription-plans';
import { ShAccountSettingsCode } from '../../enums/admin-settings';
import {
  getAdminSettingsValues,
  getEmailVerificationModalContent,
  getProspectDuplicationAllowedModalContent,
  getToggleOnOrOffMessage,
  getProspectSharingModalContent,
  getProspectSharingOnOffMessage,
  getAutoAddToProspectOnLeadRevealOnOffMessage,
  getAutoAddToProspectOnLeadRevealModalContent,
  checkLifeTimeSubcriptionPlan,
} from './utils/helper';
import { IProps, PaginationOptions } from './types';

import { getIsRequestPending } from '../../../../shared/utils/get-request-status';
import { SpinnerAnimation } from '../../../../shared/design-system/components/atoms/spinner/spinner';
import ConfirmationModalV3 from '../../../../shared/design-system/components/atoms/confirmation-modal/confirmation-modal-v3';
import { ConfirmationModalIconType } from '../../../../shared/design-system/components/atoms/confirmation-modal/enum';
import { GlobalSettings } from '../../../../shared/components/global-settings-wrapper';
import SequenceSetting from './components/admin-setting-sequence';
import AdminSettingSection from './components/admin-setting-section';
import { supportUrls } from '../../../../shared/utils/urls';
import AdminSettingExclusionInput from './components/admin-setting-exclusion-input';
import { getExclusionListPaginationInitialState } from './admin-settings-slice';
import {
  OverlayTooltip,
  Placement,
} from '../../../../shared/design-system/components/overlay';
import { Routes } from '../../../../shared/routes';
import Input from '../../../../shared/design-system/components/input';
import EmailsAndDomainsInput from '../do-not-contact/components/do-not-contact-list-details/components/do-not-contact-list-details-input/emails-and-domains-input';
import DataTable from '../../../../shared/components/data-table/data-table';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import hasPermission from '../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../shared/utils/access-control/enums/permissions';
import { isAgencyUser } from '../../../../shared/utils/user-details';
import ProspectTagsRow from '../../../../shared/design-system/components/molecules/prospect-tags-row';
import SmartReverificationSettings from './components/smart-reverification-settings/smart-reverification-settings';
import RadioButtonGroup from '../../../../shared/design-system/components/radio-button-group';

const AdminSettings: React.FC<IProps> = ({
  adminSettings,
  resetAdminSettings,

  getAdminSettingsRequestStatus,
  getAdminSettingsRequestError,
  sendGetAdminDetailsRequest,

  updateAdminSettingsStatus,
  updateAdminSettingsMessage,
  updateAdminSettingsError,
  sendUpdateAdminDetailsRequest,
  resetUpdateAdminSettings,

  integrationAdminSetting,
  getIntegrationAdminSettingRequestStatus,
  changeIntegrationSettingRequestStatus,
  getIntegrationSetting,
  sendChangeIntegrationSetting,
  resetChangeIntegrationSettingRequest,

  sendUpdateSSOOrganizationRequest,
  updateSSOOrganizationRequestStatus,
  updateSSOOrganizationRequestError,

  sendGetSSOOrganizationRequest,

  sendGetSSOSetupUrlRequest,
  getSSOSetupUrlRequestStatus,
  getSSOSetupUrlRequestError,

  subscriptionPlan,
  agencyConfig,
  atmRole,
  ssoSetupUrl,
  organizationData,
  organizationId,

  sendGetExclusionListRequest,
  exclusionList,
  exclusionListPaginationOptions,
  resetExclusionList,
  sendGetUserSettingsRequest,

  updateIsFirstStepTextOnlyEmail,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const shouldFetchExclusionList = useRef<boolean>(true);

  const [isInitialRequest, setIsInitialRequest] = useState(true);
  const [emailVerificationModal, setEmailVerificationModal] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [prospectDuplicationModal, setProspectDuplicationModal] = useState(
    false,
  );
  const [
    firstStepTextOnlyEmailModal,
    setFirstStepTextOnlyEmailModal,
  ] = useState(false);
  const isUpdatingFirstStepTextOnlyEmailModal = useRef<boolean>(false);

  const [
    isProspectSharingConfirmationModalVisible,
    setIsProspectSharingConfirmationModalVisible,
  ] = useState(false);
  const [
    autoAddToProspectOnLeadRevealConfirmationModalVisible,
    setAutoAddToProspectOnLeadRevealConfirmationModalVisible,
  ] = useState(false);

  const [integrationSetting, setIntegrationSetting] = useState(
    integrationAdminSetting,
  );

  const [showSSOEnableWarningModal, setShowSSOEnableWarningModal] = useState(
    false,
  );

  const [ssoDisplayName, setSSODisplayName] = useState<string>('');
  const [isSSODisplayNameError, setIsSSODisplayNameError] = useState<boolean>(
    false,
  );
  const [ssoDomains, setSSODomains] = useState<string[]>([]);
  const [isSSODomainError, setIsSSODomainError] = useState<boolean>(false);

  const [showSSOSetUpModal, setShowSSOSetUpModal] = useState(false);

  const [showSSOSetupLinkModal, setShowSSOSetupLinkModal] = useState(false);
  const [isUserRequestedSSOUrl, setIsUserRequestedSSOUrl] = useState(false);
  const [isSSOSetupInProgress, setIsSSOSetupInProgress] = useState(false);

  const [
    isProspectRestrictionPending,
    setIsProspectRestrictionPending,
  ] = useState(false);
  const [localRestrictionRadio, setLocalRestrictionRadio] = useState<'A' | 'B'>(
    'B',
  );
  // Optimistic OFF state — set immediately on toggle-off click so the UI
  // reflects OFF during the API call (prevents save button showing as loading)
  const [
    isProspectToggleLocallyOff,
    setIsProspectToggleLocallyOff,
  ] = useState(false);

  const {
    isVerificationActive,
    prospectDuplicationAllowed,
    activeProspectDuplicationAllowed,
    prospectSharing,
    AutoAddToProspectOnLeadReveal,
    isAICategorization,
    trackUniboxOpen,
    trackUniboxClick,
    isAddProspectFromBusinessDomain,
    trackExternalMails,
    enableExclusionList,
    isSSOEnabled,
    isMFAEnabled,
    firstStepTextOnlyEmail,
    emailVerificationDelayDays,
  } = getAdminSettingsValues(adminSettings);

  const hasSSOValidPlan = (subPlan: string): boolean =>
    subPlan?.indexOf('outreach-scale') > -1;

  // Prospect duplication: toggle + radio
  // Toggle ON  = Duplicate=1 (allow duplicates in multiple sequences)
  // Toggle OFF = Duplicate=0
  // Radio A (default) = Only if inactive  → ActiveStatus=0, Duplicate=1
  // Radio B           = Regardless        → ActiveStatus=1, Duplicate=1
  const isProspectRestrictionToggleOnFromBackend = prospectDuplicationAllowed;
  // Radio B = Regardless (ActiveStatus=1), Radio A = Only if inactive (ActiveStatus=0)
  const backendSelectedRadio: 'A' | 'B' = activeProspectDuplicationAllowed
    ? 'B'
    : 'A';
  const isProspectRestrictionToggleOn =
    (isProspectRestrictionToggleOnFromBackend || isProspectRestrictionPending) &&
    !isProspectToggleLocallyOff;
  // Save enabled when toggle is ON AND (local selection differs from backend OR toggle is pending)
  const isProspectSaveBtnEnabled =
    isProspectRestrictionToggleOn &&
    (isProspectRestrictionPending ||
      localRestrictionRadio !== backendSelectedRadio);

  const handleProspectRestrictionToggle = () => {
    if (
      getIsRequestPending(updateAdminSettingsStatus) ||
      getIsRequestPending(getAdminSettingsRequestStatus)
    ) {
      return;
    }
    if (isProspectRestrictionToggleOn) {
      if (isProspectRestrictionPending) {
        setIsProspectRestrictionPending(false);
      } else {
        // Confirmed ON → OFF: reflect OFF immediately then call API
        setIsProspectToggleLocallyOff(true);
        sendUpdateAdminDetailsRequest({
          settings: [
            {
              code: ShAccountSettingsCode.ProspectDuplicationAllowed,
              value: '0',
            },
            {
              code: ShAccountSettingsCode.ActiveProspectDuplicationAllowed,
              value: '0',
            },
          ],
        });
      }
    } else {
      // OFF → ON: pre-select Radio B, show Save
      setIsProspectRestrictionPending(true);
      setLocalRestrictionRadio('B');
    }
  };

  const handleRestrictionRadioChange = (val: 'A' | 'B') => {
    if (
      getIsRequestPending(updateAdminSettingsStatus) ||
      getIsRequestPending(getAdminSettingsRequestStatus)
    ) {
      return;
    }
    setLocalRestrictionRadio(val);
  };

  const handleProspectRestrictionSave = () => {
    if (
      getIsRequestPending(updateAdminSettingsStatus) ||
      getIsRequestPending(getAdminSettingsRequestStatus)
    ) {
      return;
    }
    if (localRestrictionRadio === 'A') {
      // Only if inactive → ActiveStatus=0, Duplicate=1
      sendUpdateAdminDetailsRequest({
        settings: [
          {
            code: ShAccountSettingsCode.ActiveProspectDuplicationAllowed,
            value: '0',
          },
          {
            code: ShAccountSettingsCode.ProspectDuplicationAllowed,
            value: '1',
          },
        ],
      });
    } else {
      // Regardless → ActiveStatus=1, Duplicate=1
      sendUpdateAdminDetailsRequest({
        settings: [
          {
            code: ShAccountSettingsCode.ActiveProspectDuplicationAllowed,
            value: '1',
          },
          {
            code: ShAccountSettingsCode.ProspectDuplicationAllowed,
            value: '1',
          },
        ],
      });
    }
  };

  // Scroll functionality
  const handleScrollToSection = (sectionId: string, shouldCleanUrl = false) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Clean up URL parameter after scrolling
      if (shouldCleanUrl) {
        const { search } = location;
        const queryParams = new URLSearchParams(search);
        queryParams.delete('scrollTo');

        const newSearch = queryParams.toString();
        const newUrl = `${location.pathname}${
          newSearch ? `?${newSearch}` : ''
        }`;

        history.replace(newUrl);
      }
    }
  };

  const onHideEmailVerificationModal = () => {
    setEmailVerificationModal(false);
  };

  const onHideProspectDuplicationModal = () => {
    setProspectDuplicationModal(false);
  };

  const onHideFirstStepTextOnlyEmailModal = () => {
    setFirstStepTextOnlyEmailModal(false);
  };

  const onHideProspectSharingModal = () => {
    setIsProspectSharingConfirmationModalVisible(false);
  };

  const onToggle = (code: ShAccountSettingsCode) => {
    if (code === ShAccountSettingsCode.IsVerificationActive) {
      setEmailVerificationModal(true);
      return;
    }
    if (code === ShAccountSettingsCode.ProspectDuplicationAllowed) {
      setProspectDuplicationModal(true);
      return;
    }
    if (code === ShAccountSettingsCode.ProspectSharing) {
      setIsProspectSharingConfirmationModalVisible(true);
      return;
    }
    if (code === ShAccountSettingsCode.AutoAddToProspectOnLeadReveal) {
      setAutoAddToProspectOnLeadRevealConfirmationModalVisible(true);
      return;
    }
    if (code === ShAccountSettingsCode.IntegrationSetting) {
      sendChangeIntegrationSetting(integrationSetting === 1);
      return;
    }
    if (code === ShAccountSettingsCode.FirstStepTextOnlyEmail) {
      setFirstStepTextOnlyEmailModal(true);
    }
  };

  const onSettingToggle = (code: ShAccountSettingsCode, value: boolean) => {
    if (
      getIsRequestPending(updateAdminSettingsStatus) ||
      getIsRequestPending(getAdminSettingsRequestStatus)
    ) {
      return;
    }

    const settings = [
      {
        code,
        value: value ? '1' : '0',
      },
    ];

    sendUpdateAdminDetailsRequest({
      settings,
    });
  };

  const handleUpdateIsVerificationActive = () => {
    if (
      getIsRequestPending(updateAdminSettingsStatus) ||
      getIsRequestPending(getAdminSettingsRequestStatus)
    ) {
      return;
    }

    if (isVerificationActive) {
      const settings = [
        {
          code: ShAccountSettingsCode.IsVerificationActive,
          value: '0',
        },
        {
          code: ShAccountSettingsCode.EmailVerificationDelayDays,
          value: '',
        },
      ];
      sendUpdateAdminDetailsRequest({ settings });
      return;
    }

    const settings = [
      {
        code: ShAccountSettingsCode.IsVerificationActive,
        value: '1',
      },
    ];
    sendUpdateAdminDetailsRequest({ settings });
  };

  const handleUpdateVerificationSkipInterval = (days: string) => {
    if (
      getIsRequestPending(updateAdminSettingsStatus) ||
      getIsRequestPending(getAdminSettingsRequestStatus)
    ) {
      return;
    }

    const settings = [
      {
        code: ShAccountSettingsCode.EmailVerificationDelayDays,
        value: days,
      },
    ];

    sendUpdateAdminDetailsRequest({
      settings,
    });
  };

  const handleGetExclusionListRequest = (
    paginationOption: PaginationOptions,
  ) => {
    sendGetExclusionListRequest(paginationOption);
  };

  const onShowSSOSetUpModal = () => {
    if (organizationData) {
      setSSODisplayName(organizationData.displayName);
      setSSODomains(organizationData.domains);
    }
    setShowSSOSetUpModal(true);
  };

  const getSSOTogleLabel = (subPlan: string) => {
    if (hasSSOValidPlan(subPlan)) {
      return (
        <span>
          Enable Single-Sign On for seamless access for all the users with just
          one login{' '}
          <a href={supportUrls.ssoSetUpBlog} target="_blank" rel="noreferrer">
            Learn More
          </a>
        </span>
      );
    }
    return (
      <span className="sso-toggle-label">
        <span className="sso-toggle-label-disable">
          To enable SSO Upgrade your plan to
        </span>
        <Link to={Routes.Setting_Billing_Upgrade_Email_Outreach_Plan}>
          Scale or above
        </Link>
      </span>
    );
  };

  const getSSOSetUpContent = (): ReactNode => (
    <div className="sso-setup-form-container">
      <form>
        <Input
          name="displayName"
          label="Display name"
          placeholder="Enter display name"
          value={ssoDisplayName}
          onChange={(value) => {
            setSSODisplayName(value);
            setIsSSODisplayNameError(false);
          }}
          className="admin-setting-input"
          variant={isSSODisplayNameError && Input.Variant.Error}
          caption={isSSODisplayNameError && 'Display name is required'}
        />

        <div className="do-not-contact-list-details--input-wrapper admin-setting-sso-domain--input">
          <div className="admin-setting-sso-input--label-wrapper">
            <span className="admin-setting-sso-input--label">
              Authorized Domain for Login
            </span>
            <OverlayTooltip
              text="SSO app will only allow SAML logins from users whose email are in this list of domains"
              placement={Placement.Right}
            >
              <InfoCircle color="#6B7280" />
            </OverlayTooltip>
          </div>

          <EmailsAndDomainsInput
            emailsAndDomains={ssoDomains}
            setEmailsAndDomains={setSSODomains}
            isError={isSSODomainError}
            setIsError={setIsSSODomainError}
            isMaxedEmailAdded={false}
            placeholder="Enter a domain"
          />
        </div>
      </form>
    </div>
  );

  const getSSOSetupLinkContent = (): ReactNode => (
    <div className="sso-setup-link-container">
      <p className="sso-setup-link-container--text">
        Make sure you copy this link to configure your SAML settings, as it’s
        valid for one time use only.
      </p>
      <div className="copy-content">
        <span>{ssoSetupUrl}</span>
      </div>
    </div>
  );

  const getSSOLoginColumns = () => [
    {
      title: 'Organization ID',
      dataIndex: 'organizationId',
      key: 'organizationId',
      align: 'left',
      sticky: true,
    },
    {
      title: 'Display Name',
      dataIndex: 'displayName',
      key: 'displayName',
      align: 'left',
    },
    {
      title: 'Account ID',
      dataIndex: 'accountId',
      key: 'accountId',
      align: 'left',
    },
    {
      title: 'Domains',
      dataIndex: 'domains',
      key: 'domains',
      align: 'left',
      render: (cell) => <ProspectTagsRow tags={cell} />,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'left',
      render: (status: boolean) => (
        <div className="sso-status">
          {status ? (
            <>
              <BadgeCheck height="20" width="20" color="green" />
              <span>Configured</span>
            </>
          ) : (
            <>
              <BadgeCross height="20" width="20" color="red" />
              <span>Not Configured</span>
            </>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      align: 'left',
      render: () => (
        <div className="sso-actions">
          {organizationData.isSSOActiveAndEnabled ? (
            <div className="sso-actions--refresh-disabled">
              <Refresh height="20" width="20" />
            </div>
          ) : (
            <OverlayTooltip text="Refresh">
              <div
                {...accessibleOnClick(() => sendGetSSOOrganizationRequest())}
              >
                <Refresh height="20" width="20" />
              </div>
            </OverlayTooltip>
          )}
          <OverlayTooltip text="Edit">
            <div {...accessibleOnClick(() => onShowSSOSetUpModal())}>
              <Pencil height="20" width="20" />
            </div>
          </OverlayTooltip>
        </div>
      ),
    },
  ];

  const getSSOLoginData = () => {
    const domains = organizationData.domains?.map((domain) => ({
      name: domain,
    }));
    return [
      {
        organizationId: organizationData.organizationId,
        displayName: organizationData.displayName,
        accountId: organizationData.accountId,
        domains,
        status: organizationData.isSSOActiveAndEnabled,
      },
    ];
  };

  useEffect(() => {
    sendGetAdminDetailsRequest();
    if (hasPermission(Permissions.INTEGRATION_WRITE)) getIntegrationSetting();
  }, []);

  // Handle URL parameters for scrolling to specific sections
  useEffect(() => {
    const { search } = location;
    const queryParams = new URLSearchParams(search);
    const scrollTo = queryParams.get('scrollTo');

    if (scrollTo) {
      // Wait for component to be fully loaded, then scroll once
      setTimeout(() => {
        handleScrollToSection(scrollTo, true);
      }, 1000);
    }
  }, [location, isInitialRequest]);

  // Sync local radio selection from backend (when not in pending/unsaved state)
  // Also clear the optimistic-off flag once backend confirms Duplicate=0
  useEffect(() => {
    if (!isProspectRestrictionPending) {
      setLocalRestrictionRadio(activeProspectDuplicationAllowed ? 'B' : 'A');
    }
    if (!prospectDuplicationAllowed) {
      setIsProspectToggleLocallyOff(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProspectDuplicationAllowed, prospectDuplicationAllowed]);

  useEffect(() => {
    executeOnRequestStatus({
      status: updateAdminSettingsStatus,
      onSuccess: () => {
        toaster.success(toastMessage || updateAdminSettingsMessage, {
          theme: Theme.New,
        });
        setToastMessage('');
        if (!isSSOSetupInProgress) {
          sendGetAdminDetailsRequest();
        }
        resetUpdateAdminSettings();
        setIsProspectRestrictionPending(false);
        emailVerificationModal && setEmailVerificationModal(false);
        prospectDuplicationModal && setProspectDuplicationModal(false);

        if (firstStepTextOnlyEmailModal) {
          setFirstStepTextOnlyEmailModal(false);
        }

        if (isUpdatingFirstStepTextOnlyEmailModal?.current) {
          updateIsFirstStepTextOnlyEmail();
          isUpdatingFirstStepTextOnlyEmailModal.current = false;
        }

        isProspectSharingConfirmationModalVisible &&
          setIsProspectSharingConfirmationModalVisible(false);
        autoAddToProspectOnLeadRevealConfirmationModalVisible &&
          setAutoAddToProspectOnLeadRevealConfirmationModalVisible(false);
        if (ssoSetupUrl && showSSOSetUpModal) {
          setShowSSOSetUpModal(false);
          setShowSSOSetupLinkModal(true);
        }
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: updateAdminSettingsError,
          onError: () => {
            showGeneralErrorNotification(updateAdminSettingsError.message);
            resetUpdateAdminSettings();
          },
        });
      },
    });
  }, [updateAdminSettingsStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getIntegrationAdminSettingRequestStatus,
      onSuccess: () => {
        setIntegrationSetting(integrationAdminSetting);
      },
    });
  }, [getIntegrationAdminSettingRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: changeIntegrationSettingRequestStatus,
      onSuccess: () => {
        toaster.success('Admin settings updated successfully', {
          theme: Theme.New,
        });
        getIntegrationSetting();
        resetChangeIntegrationSettingRequest();
      },
      onFailed: () => {
        resetChangeIntegrationSettingRequest();
      },
    });
  }, [changeIntegrationSettingRequestStatus]);

  useEffect(
    () => () => {
      resetAdminSettings();
      resetExclusionList();
    },
    [],
  );

  useEffect(() => {
    executeOnRequestStatus({
      status: getAdminSettingsRequestStatus,
      onSuccess: () => {
        setIsInitialRequest(false);

        if (
          enableExclusionList &&
          trackExternalMails &&
          shouldFetchExclusionList.current
        ) {
          shouldFetchExclusionList.current = false;
          handleGetExclusionListRequest(
            getExclusionListPaginationInitialState(),
          );
        }

        if (
          isSSOEnabled &&
          hasPermission(Permissions.ACCOUNT_SSO_SETTING_READ)
        ) {
          sendGetSSOOrganizationRequest();
        }
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: getAdminSettingsRequestError,
          onError: () => {
            showGeneralErrorNotification(getAdminSettingsRequestError.message);
          },
        });
      },
    });
  }, [getAdminSettingsRequestStatus]);

  const handleLoadMore = () => {
    if (
      exclusionListPaginationOptions.currentPage >=
      exclusionListPaginationOptions.totalPages
    ) {
      return;
    }
    if (
      exclusionListPaginationOptions.currentPage <
      exclusionListPaginationOptions.totalPages
    ) {
      handleGetExclusionListRequest({
        ...getExclusionListPaginationInitialState(),
        currentPage: exclusionListPaginationOptions.currentPage + 1,
      });
    }
  };

  const handleSSOOrganizationCreation = () => {
    if (ssoDisplayName === '') {
      setIsSSODisplayNameError(true);
      return;
    }

    if (ssoDomains.length === 0) {
      setIsSSODomainError(true);
      return;
    }

    if (isSSOEnabled) {
      sendUpdateSSOOrganizationRequest({
        displayName: ssoDisplayName,
        domains: ssoDomains,
      });
    } else {
      if (
        getIsRequestPending(updateAdminSettingsStatus) ||
        getIsRequestPending(getAdminSettingsRequestStatus)
      ) {
        return;
      }

      const settings = [
        {
          code: ShAccountSettingsCode.IsSSOEnabled,
          value: isSSOEnabled ? '0' : '1',
        },
      ];

      const ssoOrganizationData = [
        {
          displayName: ssoDisplayName,
          domains: ssoDomains,
        },
      ];

      sendUpdateAdminDetailsRequest({
        settings,
        ssoOrganizationData,
      });
    }
  };

  const handleGetSSOSetupUrl = () => {
    setIsUserRequestedSSOUrl(true);
    sendGetSSOSetupUrlRequest();
  };

  const handleSSOSetupLinkModalonClose = () => {
    setShowSSOSetupLinkModal(false);
    setIsSSOSetupInProgress(false);
    sendGetAdminDetailsRequest();
    sendGetUserSettingsRequest();
  };

  const handleToggleSSO = () => {
    if (!isSSOEnabled) {
      setShowSSOEnableWarningModal(true);
      if (!organizationId) {
        setIsSSOSetupInProgress(true);
      }
    } else {
      onSettingToggle(ShAccountSettingsCode.IsSSOEnabled, !isSSOEnabled);
    }
  };

  const handleSSOEnableWarningSumbit = () => {
    if (organizationId) {
      onSettingToggle(ShAccountSettingsCode.IsSSOEnabled, !isSSOEnabled);
      setShowSSOEnableWarningModal(false);
    } else {
      setShowSSOEnableWarningModal(false);
      onShowSSOSetUpModal();
    }
  };

  useEffect(() => {
    executeOnRequestStatus({
      status: getSSOSetupUrlRequestStatus,
      onSuccess: () => {
        if (isUserRequestedSSOUrl) {
          setShowSSOSetupLinkModal(true);
          setIsUserRequestedSSOUrl(false); // Reset for next time
        }
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: getSSOSetupUrlRequestError,
          onError: () => {
            showGeneralErrorNotification(getSSOSetupUrlRequestError.message);
          },
        });
      },
    });
  }, [getSSOSetupUrlRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: updateSSOOrganizationRequestStatus,
      onSuccess: () => {
        setShowSSOSetUpModal(false);
        sendGetAdminDetailsRequest();
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: updateSSOOrganizationRequestError,
          onError: () => {
            showGeneralErrorNotification(
              updateSSOOrganizationRequestError.message,
            );
            resetAdminSettings();
          },
        });
      },
    });
  }, [updateSSOOrganizationRequestStatus]);

  return (
    <>
      <GlobalSettings.Header>
        <GlobalSettings.HeaderLeft>
          <GlobalSettings.HeaderTitle title="Admin Settings" />
        </GlobalSettings.HeaderLeft>
      </GlobalSettings.Header>
      {subscriptionPlan === SubscriptionPlans.FREE && <HeaderBanner />}
      <GlobalSettings.Content className="admin-settings">
        {getIsRequestPending(getAdminSettingsRequestStatus) &&
        isInitialRequest ? (
          <div className="admin-settings--spinner">
            <Spinner animation={SpinnerAnimation.Border} />
          </div>
        ) : (
          <div className="admin-settings--content">
            <AdminSettingSection title="Sequence">
              <AdminSettingToggle
                checked={firstStepTextOnlyEmail}
                onChange={(code) => {
                  if (firstStepTextOnlyEmail) {
                    isUpdatingFirstStepTextOnlyEmailModal.current = true;
                    onSettingToggle(
                      ShAccountSettingsCode.FirstStepTextOnlyEmail,
                      false,
                    );
                  } else {
                    onToggle(code);
                  }
                }}
                code={ShAccountSettingsCode.FirstStepTextOnlyEmail}
                label="Send first email as text only"
                tooltipOnInfoIcon="Once enabled, any images in the first email will be removed. HTML links will also be disabled, all type of formatting will be removed and emails will be sent in plain text."
              />
              <SequenceSetting setToastMessage={setToastMessage} />
            </AdminSettingSection>
            <AdminSettingSection
              title={
                <div className='d-flex align-items-center'>
                  Email Tracking and Reply Management
                  <OverlayTooltip
                    text="Manage tracking and replies for all the emails sent from Unified Inbox or the Prospect Profile’s Email tab."
                    className="unified-inbox-tooltip"
                  >
                    <InfoCircle
                      width={14}
                      height={14}
                      className="cursor-pointer ml-2"
                    />
                  </OverlayTooltip>
                </div>
              }
            >
              <AdminSettingToggle
                checked={isAICategorization}
                onChange={() =>
                  onSettingToggle(
                    ShAccountSettingsCode.AICategorization,
                    !isAICategorization,
                  )
                }
                code={ShAccountSettingsCode.AICategorization}
                label="AI Categorization in Unified Inbox : Assign outcome to conversation based on reply content"
              />
              <AdminSettingToggle
                checked={trackUniboxOpen}
                onChange={() =>
                  onSettingToggle(
                    ShAccountSettingsCode.TrackUniboxOpen,
                    !trackUniboxOpen,
                  )
                }
                code={ShAccountSettingsCode.TrackUniboxOpen}
                label="Track opens for all one-to-one emails sent."
              />
              <AdminSettingToggle
                checked={trackUniboxClick}
                onChange={() =>
                  onSettingToggle(
                    ShAccountSettingsCode.TrackUniboxClick,
                    !trackUniboxClick,
                  )
                }
                code={ShAccountSettingsCode.TrackUniboxClick}
                label="Track clicks for all one-to-one emails sent."
              />
              <AdminSettingToggle
                checked={trackExternalMails}
                onChange={() => {
                  shouldFetchExclusionList.current = true;
                  onSettingToggle(
                    ShAccountSettingsCode.TrackExternalMails,
                    !trackExternalMails,
                  );
                }}
                code={ShAccountSettingsCode.TrackExternalMails}
                label="Track external replies"
                learnMoreLink={supportUrls.trackExternalEmailsBlog}
                nestedSettings={[
                  {
                    label: (
                      <>
                        Track all replies from recipients with the same domain
                        as your prospects
                        <OverlayTooltip
                          placement={Placement.RightStart}
                          text="For e.g. If your prospect is jake@abc.com, replies from john@abc.com will be tracked and shown in unified inbox as they have matching domain."
                          className="external-reply-tooltip"
                        >
                          <InfoCircle
                            width={14}
                            height={14}
                            className="all-replies-icon"
                          />
                        </OverlayTooltip>
                      </>
                    ),
                    checked: isAddProspectFromBusinessDomain,
                    onChange: () => {
                      onSettingToggle(
                        ShAccountSettingsCode.AddProspectFromBusinessDomain,
                        !isAddProspectFromBusinessDomain,
                      );
                    },
                    disabled: !trackExternalMails,
                  },
                  {
                    label: (
                      <>
                        Do not receive emails from specific domains or emails
                        <OverlayTooltip
                          placement={Placement.RightStart}
                          text='You can add a domain/email address below. For example, adding "example.com" will not show any external emails from that domain in your unified inbox. '
                          className="external-reply-tooltip"
                        >
                          <InfoCircle
                            width={14}
                            height={14}
                            className="all-replies-icon"
                          />
                        </OverlayTooltip>
                      </>
                    ),
                    checked: enableExclusionList,
                    onChange: () => {
                      shouldFetchExclusionList.current = true;
                      return onSettingToggle(
                        ShAccountSettingsCode.EnableExclusionList,
                        !enableExclusionList,
                      );
                    },
                    disabled: !trackExternalMails,
                  },
                ]}
              />

              {enableExclusionList && trackExternalMails && (
                <AdminSettingExclusionInput
                  exclusionList={exclusionList}
                  handleLoadMore={handleLoadMore}
                />
              )}
            </AdminSettingSection>

            {!isAgencyUser() && (
              <AdminSettingSection title="Lead Finder">
                <AdminSettingToggle
                  checked={AutoAddToProspectOnLeadReveal}
                  onChange={onToggle}
                  code={ShAccountSettingsCode.AutoAddToProspectOnLeadReveal}
                  label="Save revealed leads from Lead Finder as prospects automatically"
                />
              </AdminSettingSection>
            )}

            <AdminSettingSection title="Prospects" id="prospects">
              <div>
                <AdminSettingToggle
                  checked={isProspectRestrictionToggleOn}
                  onChange={handleProspectRestrictionToggle}
                  code={ShAccountSettingsCode.ProspectDuplicationAllowed}
                  label="Allow adding duplicate prospects in multiple sequences"
                />
                <div
                  className={`prospect-restriction--radio-group${
                    !isProspectRestrictionToggleOn ? ' disabled' : ''
                  }`}
                >
                  <RadioButtonGroup
                    name="prospect-restriction"
                    value={isProspectRestrictionToggleOn ? localRestrictionRadio : ''}
                    onChange={(val) => handleRestrictionRadioChange(val)}
                    className="radio-vertical"
                    options={[
                      {
                        value: 'A',
                        label:
                          'Only when prospect status is inactive, completed or finished in other sequences.',
                        disabled: !isProspectRestrictionToggleOn,
                      },
                      {
                        value: 'B',
                        label:
                          'Regardless of the prospect status in other sequences.',
                        disabled: !isProspectRestrictionToggleOn,
                      },
                    ]}
                  />
                  <Button
                    onClick={handleProspectRestrictionSave}
                    disabled={
                      !isProspectSaveBtnEnabled ||
                      getIsRequestPending(updateAdminSettingsStatus)
                    }
                    isLoading={
                      isProspectRestrictionToggleOn &&
                      getIsRequestPending(updateAdminSettingsStatus)
                    }
                    loadingText="Saving..."
                  >
                    Save
                  </Button>
                </div>
              </div>

              <AdminSettingToggle
                checked={isVerificationActive}
                onChange={onToggle}
                code={ShAccountSettingsCode.IsVerificationActive}
                label="Allow verifying prospects while importing"
              />

              <SmartReverificationSettings
                isVerificationActive={isVerificationActive}
                emailVerificationDelayDays={emailVerificationDelayDays}
                handleUpdateVerificationSkipInterval={
                  handleUpdateVerificationSkipInterval
                }
                isLoading={getIsRequestPending(updateAdminSettingsStatus)}
              />
            </AdminSettingSection>

            {agencyConfig?.agencyId === undefined && (
              <AdminSettingSection title="Team Management">
                <AdminSettingToggle
                  checked={prospectSharing}
                  onChange={onToggle}
                  code={ShAccountSettingsCode.ProspectSharing}
                  label="Allow team members to see prospects of each other"
                />
              </AdminSettingSection>
            )}

            {hasPermission(Permissions.INTEGRATION_WRITE) && (
              <AdminSettingSection title="Integration and Webhooks">
                <AdminSettingToggle
                  checked={integrationSetting === 2}
                  onChange={onToggle}
                  code={ShAccountSettingsCode.IntegrationSetting}
                  label="Send Integration/Webhook events for entire account(Including user's account)"
                  tooltipOnInfoIcon="When this is turned ON, all events including member's events will be sent which might cause duplication of data."
                />
              </AdminSettingSection>
            )}

            {!isAgencyUser() && (
              <AdminSettingSection title="Security">
                {!checkLifeTimeSubcriptionPlan(subscriptionPlan) &&
                  atmRole === 'owner' && (
                    <div className="sso-toggle-container">
                      <div className="sso-toggle">
                        <AdminSettingToggle
                          onChange={handleToggleSSO}
                          checked={isSSOEnabled}
                          code={ShAccountSettingsCode.IsSSOEnabled}
                          label={getSSOTogleLabel(subscriptionPlan)}
                          isDisabled={!hasSSOValidPlan(subscriptionPlan)}
                        />
                        {isSSOEnabled && hasSSOValidPlan(subscriptionPlan) && (
                          <div
                            className="sso-setup-link"
                            {...accessibleOnClick(() => handleGetSSOSetupUrl())}
                          >
                            <span>
                              <LinkIcon height="20" width="20" />
                            </span>
                            <span>SSO Setup Link</span>
                          </div>
                        )}
                      </div>
                      {isSSOEnabled &&
                        hasSSOValidPlan(subscriptionPlan) &&
                        organizationData && (
                          <DataTable
                            columns={getSSOLoginColumns()}
                            data={getSSOLoginData()}
                          />
                        )}
                    </div>
                  )}
                <AdminSettingToggle
                  checked={isMFAEnabled}
                  onChange={() =>
                    onSettingToggle(
                      ShAccountSettingsCode.IsMFAEnabled,
                      !isMFAEnabled,
                    )
                  }
                  code={ShAccountSettingsCode.IsMFAEnabled}
                  isDisabled={isSSOEnabled}
                  label={
                    <span
                      {...(isSSOEnabled && {
                        className: 'mfa-toggle-label-disable',
                      })}
                    >
                      Require email OTP for all user logins as a Two-Factor
                      Authentication step
                    </span>
                  }
                  tooltipText={
                    isSSOEnabled &&
                    "2FA is disabled since you're using Login with SSO Method"
                  }
                />
              </AdminSettingSection>
            )}
          </div>
        )}
      </GlobalSettings.Content>

      <ConfirmationModalV3
        show={emailVerificationModal}
        title={getToggleOnOrOffMessage(isVerificationActive, t)}
        iconType={ConfirmationModalIconType.INFO_CIRCLE_YELLOW}
        content={getEmailVerificationModalContent(isVerificationActive, t)}
        onClose={onHideEmailVerificationModal}
        onSubmit={() => {
          handleUpdateIsVerificationActive();
        }}
        isSubmitLoading={getIsRequestPending(updateAdminSettingsStatus)}
        isSubmitDisabled={getIsRequestPending(updateAdminSettingsStatus)}
        submitButtonText={t('labels.yes')}
        cancelButtonText={t('labels.cancel')}
      />

      <ConfirmationModalV3
        show={firstStepTextOnlyEmailModal}
        title="Enable Text-Only First Email?"
        iconType={ConfirmationModalIconType.INFO_CIRCLE_YELLOW}
        customJSX={
          <div className="confirmation-modal-v3--body__contents">
            <p>
              This will remove all styling, images, and links from the{' '}
              <span className="font-semibold">
                first email step in every sequence
              </span>{' '}
              and will send emails in plain text. Opens and link clicks will
              also be disabled.
            </p>
            <p>
              If your first step relies on formatting, the content may appear
              distorted. Please review before sending. This change cannot be
              undone.
            </p>
          </div>
        }
        onClose={onHideFirstStepTextOnlyEmailModal}
        onSubmit={() => {
          isUpdatingFirstStepTextOnlyEmailModal.current = true;
          onSettingToggle(ShAccountSettingsCode.FirstStepTextOnlyEmail, true);
        }}
        isSubmitLoading={getIsRequestPending(updateAdminSettingsStatus)}
        isSubmitDisabled={getIsRequestPending(updateAdminSettingsStatus)}
        submitButtonText="Continue"
        cancelButtonText="Cancel"
      />

      <ConfirmationModalV3
        show={prospectDuplicationModal}
        title={getToggleOnOrOffMessage(prospectDuplicationAllowed, t)}
        iconType={ConfirmationModalIconType.INFO_CIRCLE_YELLOW}
        contents={getProspectDuplicationAllowedModalContent(
          prospectDuplicationAllowed,
          t,
        )}
        onClose={onHideProspectDuplicationModal}
        onSubmit={() =>
          onSettingToggle(
            ShAccountSettingsCode.ProspectDuplicationAllowed,
            !prospectDuplicationAllowed,
          )
        }
        isSubmitLoading={getIsRequestPending(updateAdminSettingsStatus)}
        isSubmitDisabled={getIsRequestPending(updateAdminSettingsStatus)}
        submitButtonText={t('labels.yes')}
        cancelButtonText={t('labels.cancel')}
      />

      <ConfirmationModalV3
        show={isProspectSharingConfirmationModalVisible}
        title={getProspectSharingOnOffMessage(prospectSharing, t)}
        iconType={ConfirmationModalIconType.INFO_CIRCLE_YELLOW}
        contents={getProspectSharingModalContent(prospectSharing)}
        onClose={onHideProspectSharingModal}
        onSubmit={() =>
          onSettingToggle(
            ShAccountSettingsCode.ProspectSharing,
            !prospectSharing,
          )
        }
        isSubmitLoading={getIsRequestPending(updateAdminSettingsStatus)}
        isSubmitDisabled={getIsRequestPending(updateAdminSettingsStatus)}
        submitButtonText={t('labels.yes')}
        cancelButtonText={t('labels.cancel')}
      />

      <ConfirmationModalV3
        show={autoAddToProspectOnLeadRevealConfirmationModalVisible}
        title={getAutoAddToProspectOnLeadRevealOnOffMessage(
          AutoAddToProspectOnLeadReveal,
          t,
        )}
        iconType={ConfirmationModalIconType.INFO_CIRCLE_YELLOW}
        contents={getAutoAddToProspectOnLeadRevealModalContent(
          AutoAddToProspectOnLeadReveal,
          t,
        )}
        onClose={() => {
          setAutoAddToProspectOnLeadRevealConfirmationModalVisible(false);
        }}
        onSubmit={() =>
          onSettingToggle(
            ShAccountSettingsCode.AutoAddToProspectOnLeadReveal,
            !AutoAddToProspectOnLeadReveal,
          )
        }
        isSubmitLoading={getIsRequestPending(updateAdminSettingsStatus)}
        isSubmitDisabled={getIsRequestPending(updateAdminSettingsStatus)}
        submitButtonText={t('labels.yes')}
        cancelButtonText={t('labels.cancel')}
      />

      <ConfirmationModalV3
        show={showSSOEnableWarningModal}
        title="Are you Sure you want to enable login with SSO"
        hideTitleIcon={true}
        icon={<AlertCircle />}
        contents={[
          'By enabling SSO Login, all users in your organisation will no longer be able to login using passwords and all active session will be logged out. Everyone will be required to use Login with SSO method.',
          '',
          'Please proceed with caution.',
        ]}
        onClose={() => setShowSSOEnableWarningModal(false)}
        onSubmit={handleSSOEnableWarningSumbit}
        showCloseIcon={false}
        cancelButtonText="Cancel"
        submitButtonText="Proceed"
      />

      <ConfirmationModalV3
        show={showSSOSetUpModal}
        title="Setup SSO Login"
        hideTitleIcon={true}
        showCloseIcon={false}
        onClose={() => {
          setShowSSOSetUpModal(false);
          setIsSSODisplayNameError(false);
          setIsSSODomainError(false);
        }}
        onSubmit={handleSSOOrganizationCreation}
        cancelButtonText="Cancel"
        submitButtonText={organizationData ? 'Update' : 'Create'}
        isSubmitLoading={getIsRequestPending(updateAdminSettingsStatus)}
        isSubmitDisabled={getIsRequestPending(updateAdminSettingsStatus)}
        content={getSSOSetUpContent()}
      />

      <ConfirmationModalV3
        className="sso-setup-link-modal"
        show={showSSOSetupLinkModal}
        title="SSO Setup Link"
        hideTitleIcon={true}
        showCloseIcon={false}
        onClose={handleSSOSetupLinkModalonClose}
        onSubmit={() => {
          navigator.clipboard.writeText(ssoSetupUrl);
          toaster.success('Setup Link Copied to clipboard', {
            theme: Theme.New,
            delay: 3000,
          });
        }}
        cancelButtonText="Close"
        submitButtonText="Copy"
        content={getSSOSetupLinkContent()}
      />
    </>
  );
};

export default AdminSettings;
